import classNames from "classnames";
import { Icon } from "components/atoms/icon";
import dayjs from "dayjs";
import React from "react";
import ScrollLock from "react-scrolllock";
import Transition from "react-transition-group/Transition";

import { Input } from "../../atoms/input";
import CalendarController from "./Controller";
import styles from "./Input.module.scss";

export interface DateRange {
  endDate: string | null;
  startDate: string | null;
}

const parseDate = (date: string | null): dayjs.Dayjs | null => {
  if (!date) {
    return null;
  }

  const parsedDate = dayjs(date);

  return parsedDate.isValid() ? parsedDate : null;
};

interface Props {
  allowPast?: boolean;
  earliestAllowed?: dayjs.Dayjs;
  initialGridStart?: dayjs.Dayjs;
  label?: React.ReactNode;
  noClear?: boolean;
  onChange: (range: DateRange) => void;
  value: DateRange;
}

const CalendarInput: React.FC<Props> = ({
  allowPast,
  earliestAllowed,
  initialGridStart,
  label,
  noClear,
  onChange,
  value,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const selectorRef = React.useRef<HTMLDivElement>(null);

  const updateRange = (
    newStart: dayjs.Dayjs | null,
    newEnd: dayjs.Dayjs | null,
  ): void => {
    if (onChange) {
      onChange({
        endDate: newEnd?.toISOString() || null,
        startDate: newStart?.toISOString() || null,
      });
    }

    if (newStart !== null && newEnd !== null) {
      setIsOpen(false);
    }
  };

  const startDate = parseDate(value.startDate);

  const endDate = parseDate(value.endDate);

  return (
    <div className={styles.root}>
      {typeof label !== "undefined" ? (
        <>
          <div className={styles.customLabel} onClick={() => setIsOpen(true)}>
            {label}
          </div>
          <div className={styles.calendarIconContainer}>
            <Icon
              className={styles.calendarIcon}
              name="calendar"
              color="black"
              size="large"
            />
          </div>
        </>
      ) : (
        <div className={styles.inputs}>
          {/*TODO: width 30%*/}
          <Input isFocused={isOpen && !value.startDate}>
            <div className={styles.input} onClick={() => setIsOpen(true)}>
              <div className={styles.date}>
                {startDate
                  ? startDate.format("YYYY/MM/DD")
                  : "- - - - / - - / - -"}
              </div>
            </div>
          </Input>
          <span className={styles.caption}>から</span>
          {/*TODO: width 30%*/}
          <Input isFocused={isOpen && !!startDate && !endDate}>
            <div className={styles.input} onClick={() => setIsOpen(true)}>
              <div className={styles.date}>
                {endDate ? endDate.format("YYYY/MM/DD") : "- - - - / - - / - -"}
              </div>
            </div>
          </Input>
          <span className={styles.caption}>まで</span>
        </div>
      )}
      <Transition
        in={isOpen}
        timeout={{ enter: 0, exit: 300 }}
        mountOnEnter
        unmountOnExit
      >
        {(state: string) => (
          <>
            <ScrollLock />
            <div
              className={classNames(styles.background, {
                [styles.on]: state === "entered",
                [styles.off]: state !== "entered",
              })}
              onClick={({ target }) => {
                if (
                  selectorRef.current &&
                  (target as HTMLElement).contains(selectorRef.current)
                ) {
                  setIsOpen(false);
                }
              }}
            >
              <div className={styles.selector} ref={selectorRef}>
                <CalendarController
                  close={() => setIsOpen(false)}
                  initialGridStart={initialGridStart}
                  allowPast={allowPast}
                  noClear={noClear}
                  earliestAllowed={earliestAllowed}
                  onChange={updateRange}
                  value={{ endDate, startDate }}
                />
              </div>
            </div>
          </>
        )}
      </Transition>
    </div>
  );
};

export default CalendarInput;
